"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useMutation } from "convex/react";
import { toast } from "sonner";
import { api } from "@convex/_generated/api";
import type { DemoPersona } from "@convex/constants/demo.lib";
import { formatDateTime } from "@/lib/formatting/dates";
import { handleError } from "@/lib/handle-error";

const DEMO_HOME = "/admin/demo";
const DEMO_LANDING = "/dashboard";

type DemoAction = "start" | "reset" | "exit";

function useDemoRunner() {
  const [pending, setPending] = useState<DemoAction | null>(null);
  const run = async (action: DemoAction, work: () => Promise<void>, fallbackMessage: string) => {
    setPending(action);
    try {
      await work();
      return true;
    } catch (error) {
      handleError(error, { fallbackMessage });
      return false;
    } finally {
      setPending(null);
    }
  };
  return { run, pending };
}

export function useDemoActions() {
  const router = useRouter();
  const startDemo = useMutation(api.viewAs.mutations.startDemo);
  const resetDemo = useMutation(api.viewAs.mutations.resetDemo);
  const stopViewAs = useMutation(api.viewAs.mutations.stop);
  const { run, pending } = useDemoRunner();

  const start = (persona: DemoPersona) =>
    run("start", async () => {
      await startDemo({ persona });
      router.push(DEMO_LANDING);
    }, "Could not open the demo account");

  const reset = () =>
    run("reset", async () => {
      const { persona } = await resetDemo({});
      toast.success(`Demo accounts reset ${formatDateTime(Date.now())}`);
      if (persona) router.push(DEMO_LANDING);
    }, "Could not reset the demo accounts");

  const exit = () =>
    run("exit", async () => {
      await stopViewAs({});
      router.push(DEMO_HOME);
    }, "Could not exit the demo");

  return { start, reset, exit, pending };
}
